import React, { useCallback, useEffect, useState } from 'react';
import FocusTrap from 'focus-trap-react';

import {
  ChildrenProp,
  ClassNameProp,
  DirectionProp,
  VoidCallback
} from '../common-types';

/**
 * Props for a {@link Drawer} component.
 */
export type DrawerProps = Required<ChildrenProp> &
  ClassNameProp &
  Required<DirectionProp> & {
    /**
     * Function to be called when the drawer overlay is clicked. It should contain the functionality to close the drawer
     * (unmount it). It is also called when ESC is pressed.
     */
    onOverlayClicked: VoidCallback;
    /**
     * Enables a focus trap that doesn't automatically puts focus on the first focusable element and does not allow
     * focus to leave the drawer as long as it's opened. Should only be used when there is at least one focusable
     * element inside the drawer.
     */
    enableFocusTrap?: boolean;
    /**
     * Aria-label for screen-readers to apply to the drawer content.
     */
    ariaLabel?: string;
    /**
     * Aria-labelledby for screen-readers to apply to the drawer content.
     */
    ariaLabelledby?: string;
    /**
     * Boolean flag that determines if the drawer should use a 16px border radius for the corners.
     */
    roundedCorners?: boolean;
    /**
     * Title that is used as a header for the drawer. It is appended to the top of the drawer content and has a heading
     * role with aria-label set to 2.
     */
    title?: string;
    /**
     * An element that is displayed in the upper right corner of the drawer inside the drawer header. Should mostly be a
     * {@link Tool} or {@link MenuTool} component.
     */
    tool?: JSX.Element;
    /**
     * Controls isOpen state.
     */
    open?: boolean;
  };

/**
 * A Drawer component with 4 possible direction from which it appears. The state whether the drawer is opened or closed
 * should be handled by the parent component. The drawer should close on overlay click using the {@link onOverlayClicked}
 * prop, which is also called when ESC is pressed. The opening and closing of the drawer is animated using a 0.5s
 * transition.
 *
 * @param props a {@link DrawerProps} object.
 * @return the created {@link JSX.Element}
 * @constructor
 */
export function Drawer({
  className = '',
  ariaLabel,
  ariaLabelledby,
  children,
  direction,
  onOverlayClicked,
  enableFocusTrap = false,
  roundedCorners = false,
  title = '',
  tool,
  open
}: DrawerProps): JSX.Element {
  const [isOpen, setOpen] = useState(false);
  const closeHandler = useCallback(() => {
    setOpen(false);
    setTimeout(onOverlayClicked, 500);
  }, [onOverlayClicked]);
  useEffect(() => {
    function handleEscKey(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        closeHandler();
      }
    }
    document.addEventListener('keyup', handleEscKey, false);

    return () => {
      document.removeEventListener('keyup', handleEscKey, false);
    };
  }, [closeHandler]);

  useEffect(() => {
    if (open === undefined) {
      // for animation of isOpen effect
      setOpen(true);
      return;
    }
    setOpen(open);
  }, [open]);

  const overlayClassName = ['ehs-drawer-overlay', isOpen && 'open']
    .filter(Boolean)
    .join(' ');
  const contentClassName = [
    'ehs-drawer-content',
    'content-' + direction,
    enableFocusTrap && 'focus-trap',
    className,
    roundedCorners && 'rounded',
    isOpen && 'open'
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <>
      <div className={overlayClassName} onClick={closeHandler} />
      <FocusTrap
        active={enableFocusTrap}
        focusTrapOptions={{
          initialFocus: false,
          clickOutsideDeactivates: true
        }}
      >
        <div
          className={contentClassName}
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledby}
          aria-modal="true"
          role="dialog"
        >
          {(title || tool) && (
            <div className="ehs-drawer-content-header">
              <div role="heading" aria-level={2} className="header-title">
                {title}
              </div>
              <div className="header-action">{tool}</div>
            </div>
          )}
          {children}
        </div>
      </FocusTrap>
    </>
  );
}
